/** Committing to a target, being asked to assess a restated baseline, and the scenarios the plan's
 * assumptions produce — **ACT-01** and **ACT-03**, ADR-0108.
 *
 * Until this existed `/transition` could *read* a target and could not write one: `set_target` and
 * `record_assumptions` had routes and no control, so the guarantee that a changed baseline asks for
 * an assessment was reachable from a test and from nothing else. These are the controls.
 *
 * ## What each one refuses to do
 *
 * - **It never decides that a change matters, or that it does not.** A baseline that differs from
 *   the last version comes back from the server as `needs_reassessment` with a task raised; this
 *   screen reads `standing` from the response and prints it. It does not compare the numbers
 *   itself — two copies of "did the baseline change" eventually disagree — and it shows no size.
 * - **The reduction is not recomputed.** The form that reassesses says what leaving the field empty
 *   means (the existing proportion stands) rather than offering a figure.
 * - **A scenario is modelled, and the screen says so beside every number.** It is computed from the
 *   plan's own baseline; the inventory is not read, and the sentence says that too.
 * - **An input the arithmetic did not use is named**, with the reason, beside the projection. The
 *   demo's `gas_price_2030` (GBP/MWh) is such a row, and a projection that quietly ignored it would
 *   read as though it had been considered.
 */

import { useState } from "react";

import type { PlanProjection, PlanTarget, TransitionPlan } from "../api/queries";
import {
  useReassessTarget,
  useRecordAssumptions,
  useScenarios,
  useSetTarget,
  useTargetHistory,
} from "../api/queries";
import { useReader } from "../api/profile";
import { FieldError } from "../components/FieldError";
import { Loaded, WriteFailed } from "../components/Loaded";
import { useFormChecks } from "../components/useFormChecks";
import { perHundred, timesHundred, differenceOf, sameAmount } from "../lib/decimal";
import { readDecimal } from "../lib/decimalInput";
import { factorValue, percent, statedQuantity } from "../lib/format";
import styles from "./Screen.module.css";
import own from "./Transition.module.css";
import { ApplyAsPlan, CostLane } from "./TransitionPlanExtras";

/** Today as the person reading the screen counts it. `toISOString` is UTC, which is already
 * tomorrow for an evening in California - and a recorded date is a statement a person made. */
function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** What a person types for a reduction is a percentage (`90`); the server stores a proportion (`0.9`). The
 * shift of the point is exact (`perHundred`), never a `Number`, and the percentage must lie strictly
 * between 0 and 100 - the same open interval the server refuses outside of (`Target.__post_init__`). */
function proportionOf(percentage: string): string | undefined {
  const proportion = perHundred(percentage);
  if (proportion === undefined) return undefined;
  const above = differenceOf(proportion, "0");
  const below = differenceOf("1", proportion);
  const positive = (value: string | undefined) =>
    value !== undefined && !value.startsWith("-") && !sameAmount(value, "0");
  return positive(above) && positive(below) ? factorValue(proportion) : undefined;
}

/** A stored proportion as the text a person would type into the percentage box: `0.9000` is `90`. */
function percentageOf(proportion: string): string {
  const shifted = timesHundred(proportion);
  return shifted === undefined ? "" : factorValue(shifted);
}

const PERCENT_SENTENCE =
  "Type how far below the baseline, as a percentage between 0 and 100, such as 90.";

/** Every version of one target, oldest first, with what each said and why it exists. */
export function TargetHistory({ targetKey }: { targetKey: string }) {
  const [open, setOpen] = useState(false);
  const history = useTargetHistory(targetKey, open);
  return (
    <div className={cls(own.history)}>
      <button
        type="button"
        className={styles.fixLink}
        aria-expanded={open}
        data-testid="show-target-history"
        onClick={() => setOpen(!open)}
      >
        {open ? "Hide the history" : "Show every version"}
      </button>
      {open ? (
        <Loaded query={history} of="The target's history" empty="No versions.">
          {(versions) => (
            <ol className={cls(own.versions)} data-testid="target-history">
              {versions.map((version) => (
                <li key={version.id} className={cls(own.version)} data-version={version.version}>
                  <span className={cls(own.versionHead)}>
                    Version {version.version} ·{" "}
                    {version.standing === "needs_reassessment"
                      ? "baseline restated, not yet assessed"
                      : "in force"}
                  </span>
                  <span className={cls(own.v, "num")}>
                    reduction {percent(version.reduction)} · baseline{" "}
                    {statedQuantity(version.baseline_amount)}{" "}
                    {version.baseline_unit} in {version.baseline_year}
                  </span>
                  {version.because ? (
                    <span className={cls(own.assumptionSource)}>{version.because}</span>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </Loaded>
      ) : null}
    </div>
  );
}

/** A restated baseline is waiting for a person. **Nothing has decided anything yet.** */
export function NeedsAssessment({
  target,
  mayCommit,
  reload,
}: {
  target: PlanTarget;
  mayCommit: boolean | undefined;
  reload: () => void;
}) {
  const reassess = useReassessTarget();
  const [because, setBecause] = useState("");
  const [reduction, setReduction] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);
  const [checks, attach] = useFormChecks<HTMLDivElement>();
  const reader = useReader();

  async function submit() {
    setRefusal(null);
    // An empty reduction means "the existing proportion stands"; a typed one is read as a decimal
    // string ("0,9" is refused with the sentence, never sent as a float).
    const read = readDecimal(reduction, reader?.locale);
    const proportion = read.ok && read.value !== "" ? proportionOf(read.value) : undefined;
    if (
      !checks.run([
        { field: "because", ok: because.trim() !== "", message: "Say what you decided, and why." },
        {
          field: "reduction",
          ok: read.ok && (read.value === "" || proportion !== undefined),
          message: read.ok ? PERCENT_SENTENCE : read.sentence,
        },
      ]) ||
      !read.ok
    ) {
      return;
    }
    try {
      await reassess.mutateAsync({
        key: target.key,
        because,
        reduction: read.value === "" ? null : (proportion ?? null),
      });
      setBecause("");
      setReduction("");
    } catch (error) {
      if (!checks.refuse(error)) setRefusal(error);
    }
  }

  return (
    <div className={cls(own.finding)} data-testid="needs-reassessment" role="status">
      <p>
        <strong>The baseline of this target changed, and nobody has assessed it.</strong> It now
        reads {statedQuantity(target.baseline_amount)} {target.baseline_unit} in {target.baseline_year}
        ; the earlier versions are kept. EmitIQ has not recomputed the reduction and has not judged
        whether the change matters &mdash; a task was opened for the person who made it.
      </p>
      {mayCommit === false ? (
        <p data-testid="not-permitted-reassess">
          An Admin or an Approver records the assessment: it is a statement about what the company
          now commits to.
        </p>
      ) : (
        <div className={cls(own.form)} ref={attach}>
          <label className={styles.field}>
            <span>What did you decide, and why?</span>
            <textarea
              aria-label="What did you decide, and why?"
              {...checks.field("because")}
              rows={2}
              value={because}
              onChange={(event) => {
                checks.touch("because");
                setBecause(event.target.value);
              }}
            />
            <FieldError {...checks.say("because")} />
          </label>
          <label className={styles.field}>
            <span>Reduction in percent, if it changes</span>
            <input
              aria-label="Reduction, if it changes"
              inputMode="decimal"
              {...checks.field("reduction")}
              value={reduction}
              onChange={(event) => {
                checks.touch("reduction");
                setReduction(event.target.value);
              }}
              placeholder={`Leave empty: ${percent(target.reduction)} stands`}
            />
            <FieldError {...checks.say("reduction")} />
          </label>
          <button
            type="button"
            className={styles.primary}
            disabled={reassess.isPending}
            data-testid="record-assessment"
            onClick={() => void submit()}
          >
            {reassess.isPending ? "Recording…" : "Record the assessment"}
          </button>
          <WriteFailed error={refusal} of="the assessment" onReload={reload} />
        </div>
      )}
    </div>
  );
}

/** Commit to a target, or write the next version of one. **Never an edit.** */
export function CommitTarget({
  current,
  reload,
}: {
  current: PlanTarget | undefined;
  reload: () => void;
}) {
  const set = useSetTarget();
  const [open, setOpen] = useState(current === undefined);
  const [key, setKey] = useState(current?.key ?? "");
  const [baselineYear, setBaselineYear] = useState(String(current?.baseline_year ?? ""));
  const [baselineAmount, setBaselineAmount] = useState(
    current ? factorValue(current.baseline_amount) : "",
  );
  const [baselineUnit, setBaselineUnit] = useState(current?.baseline_unit ?? "tCO2e");
  const [horizonYear, setHorizonYear] = useState(String(current?.horizon_year ?? ""));
  const [reduction, setReduction] = useState(current ? percentageOf(current.reduction) : "");
  const [coverage, setCoverage] = useState(current?.coverage ?? "");
  const [method, setMethod] = useState(current?.method ?? "");
  const [validatedBy, setValidatedBy] = useState(current?.validated_by ?? "");
  const [because, setBecause] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);
  const [checks, attach] = useFormChecks<HTMLDivElement>();
  const reader = useReader();

  async function submit() {
    setRefusal(null);
    // **The button is never greyed for these** (Q-06, G-Wadmin-14): eight boxes, and nothing said
    // which one the grey button was waiting for. The two numbers are read as decimal *strings*
    // ("4,200" -> "4200"), never through a float: a baseline is a calculation input (rule 5).
    const figure = readDecimal(baselineAmount, reader?.locale);
    const share = readDecimal(reduction, reader?.locale);
    const proportion = share.ok && share.value !== "" ? proportionOf(share.value) : undefined;
    const ok = checks.run([
      { field: "key", ok: key.trim() !== "", message: "Give the target a name, such as Net zero by 2040." },
      {
        field: "baseline_year",
        ok: /^\d{4}$/.test(baselineYear),
        message: "Type the baseline year as four digits, such as 2022.",
      },
      {
        field: "baseline_amount",
        ok: figure.ok && figure.value !== "",
        message: figure.ok ? "Type the figure measured in the baseline year, such as 4200." : figure.sentence,
      },
      { field: "baseline_unit", ok: baselineUnit.trim() !== "", message: "Say the unit, such as tCO2e." },
      {
        field: "horizon_year",
        ok: /^\d{4}$/.test(horizonYear),
        message: "Type the horizon year as four digits, such as 2040.",
      },
      {
        field: "reduction",
        ok: proportion !== undefined,
        message: share.ok ? PERCENT_SENTENCE : share.sentence,
      },
      { field: "coverage", ok: coverage.trim() !== "", message: "Say what the target covers." },
      { field: "method", ok: method.trim() !== "", message: "Say how it is measured." },
      {
        field: "because",
        ok: current === undefined || because.trim() !== "",
        message: "Say why there is a new version.",
      },
    ]);
    if (!ok || !figure.ok || !share.ok || proportion === undefined) return;
    try {
      await set.mutateAsync({
        key: key.trim(),
        baseline_year: Number(baselineYear),
        baseline_amount: figure.value,
        baseline_unit: baselineUnit.trim(),
        horizon_year: Number(horizonYear),
        reduction: proportion,
        coverage: coverage.trim(),
        method: method.trim(),
        validated_by: validatedBy.trim() === "" ? null : validatedBy.trim(),
        because: because.trim(),
      });
      setBecause("");
      setOpen(false);
    } catch (error) {
      // A field the server named is said beside that field; anything else is the general failure.
      if (!checks.refuse(error)) setRefusal(error);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        className={styles.fixLink}
        data-testid="open-commit-target"
        onClick={() => setOpen(true)}
      >
        Change the commitment
      </button>
    );
  }

  return (
    <div className={cls(own.form)} data-testid="commit-target-form" ref={attach}>
      {current === undefined ? null : (
        <p className={styles.note}>
          This writes a <strong>new version</strong> and leaves every earlier one as it was. If the
          baseline differs from the last version, the new one waits for an assessment and a task is
          opened &mdash; whatever the size of the difference.
        </p>
      )}
      <label className={styles.field}>
        <span>Name</span>
        <input
          aria-label="Target name"
          {...checks.field("key")}
          value={key}
          placeholder="Net zero by 2040"
          disabled={current !== undefined}
          onChange={(event) => {
            checks.touch("key");
            setKey(event.target.value);
          }}
        />
        <FieldError {...checks.say("key")} />
      </label>
      <label className={styles.field}>
        <span>Baseline year, and the figure measured in it</span>
        <span className={cls(own.inline)}>
          <input
            aria-label="Baseline year"
            inputMode="numeric"
            {...checks.field("baseline_year")}
            value={baselineYear}
            onChange={(event) => {
              checks.touch("baseline_year");
              setBaselineYear(event.target.value);
            }}
            placeholder="Year"
          />
          <input
            aria-label="Baseline figure"
            inputMode="decimal"
            {...checks.field("baseline_amount")}
            value={baselineAmount}
            onChange={(event) => {
              checks.touch("baseline_amount");
              setBaselineAmount(event.target.value);
            }}
            placeholder="Amount"
          />
          <input
            aria-label="Baseline unit"
            {...checks.field("baseline_unit")}
            value={baselineUnit}
            onChange={(event) => {
              checks.touch("baseline_unit");
              setBaselineUnit(event.target.value);
            }}
          />
        </span>
        <FieldError {...checks.say("baseline_year")} />
        <FieldError {...checks.say("baseline_amount")} />
        <FieldError {...checks.say("baseline_unit")} />
      </label>
      <label className={styles.field}>
        <span>Horizon year, and how far below the baseline (%)</span>
        <span className={cls(own.inline)}>
          <input
            aria-label="Horizon year"
            inputMode="numeric"
            {...checks.field("horizon_year")}
            value={horizonYear}
            onChange={(event) => {
              checks.touch("horizon_year");
              setHorizonYear(event.target.value);
            }}
            placeholder="Year"
          />
          <input
            aria-label="Reduction"
            inputMode="decimal"
            {...checks.field("reduction")}
            value={reduction}
            onChange={(event) => {
              checks.touch("reduction");
              setReduction(event.target.value);
            }}
            placeholder="90"
          />
        </span>
        <FieldError {...checks.say("horizon_year")} />
        <FieldError {...checks.say("reduction")} />
      </label>
      <label className={styles.field}>
        <span>Covering</span>
        <input
          aria-label="Covering"
          {...checks.field("coverage")}
          value={coverage}
          onChange={(event) => {
            checks.touch("coverage");
            setCoverage(event.target.value);
          }}
          placeholder="Scope 1 and 2, market-based, all entities"
        />
        <FieldError {...checks.say("coverage")} />
      </label>
      <label className={styles.field}>
        <span>Measured how</span>
        <input
          aria-label="Measured how"
          {...checks.field("method")}
          value={method}
          onChange={(event) => {
            checks.touch("method");
            setMethod(event.target.value);
          }}
          placeholder="GHG Protocol Corporate Standard"
        />
        <FieldError {...checks.say("method")} />
      </label>
      <label className={styles.field}>
        <span>Externally validated by (leave empty if it is not)</span>
        <input
          aria-label="Externally validated by"
          value={validatedBy}
          onChange={(event) => setValidatedBy(event.target.value)}
        />
      </label>
      {current === undefined ? null : (
        <label className={styles.field}>
          <span>Why is there a new version?</span>
          <textarea
            aria-label="Why is there a new version?"
            rows={2}
            {...checks.field("because")}
            value={because}
            onChange={(event) => {
              checks.touch("because");
              setBecause(event.target.value);
            }}
            placeholder="The 2022 baseline was restated after the Leeds acquisition."
          />
          <FieldError {...checks.say("because")} />
        </label>
      )}
      <button
        type="button"
        className={styles.primary}
        disabled={set.isPending}
        data-testid="commit-target"
        onClick={() => void submit()}
      >
        {set.isPending ? "Recording…" : current === undefined ? "Commit to it" : "Record the version"}
      </button>
      {current === undefined ? null : (
        <button
          type="button"
          className={styles.fixLink}
          data-testid="cancel-commit-target"
          onClick={() => {
            setOpen(false);
            setRefusal(null);
            checks.clear();
          }}
        >
          Cancel
        </button>
      )}
      <WriteFailed error={refusal} of="the target" onReload={reload} />
    </div>
  );
}

/** Record a new version of the forecast inputs by changing or adding **one**. The rest carry over. */
export function RecordAssumption({ plan, reload }: { plan: TransitionPlan; reload: () => void }) {
  const record = useRecordAssumptions();
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState("multiplier");
  const [source, setSource] = useState("");
  const [because, setBecause] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);
  const first = plan.assumptions_version === null;
  const [checks, attach] = useFormChecks<HTMLDivElement>();
  const reader = useReader();

  async function submit() {
    setRefusal(null);
    const read = readDecimal(value, reader?.locale);
    const ok = checks.run([
      { field: "key", ok: key.trim() !== "", message: "Name the input, such as energy_price.gas." },
      {
        field: "value",
        ok: read.ok && read.value !== "",
        message: read.ok ? "Type the value, such as 0.92." : read.sentence,
      },
      { field: "unit", ok: unit.trim() !== "", message: "Say the unit. Use multiplier for an effect on the baseline." },
      { field: "source", ok: source.trim() !== "", message: "Say where it comes from." },
      { field: "because", ok: first || because.trim() !== "", message: "Say why there is a new set of assumptions." },
    ]);
    if (!ok || !read.ok) return;
    const carried = plan.assumptions
      .filter((a) => a.key !== key.trim())
      .map((a) => ({ key: a.key, value: a.value, unit: a.unit, source: a.source }));
    try {
      await record.mutateAsync({
        values: [
          ...carried,
          { key: key.trim(), value: read.value, unit: unit.trim(), source: source.trim() },
        ],
        recorded_on: today(),
        because: because.trim(),
      });
      setKey("");
      setValue("");
      setSource("");
      setBecause("");
    } catch (error) {
      if (!checks.refuse(error)) setRefusal(error);
    }
  }

  return (
    <div className={cls(own.form)} data-testid="record-assumption-form" ref={attach}>
      <label className={styles.field}>
        <span>Which input? (name an existing one to change it)</span>
        <input
          aria-label="Assumption name"
          list="assumption-keys"
          {...checks.field("key")}
          value={key}
          onChange={(event) => {
            checks.touch("key");
            setKey(event.target.value);
          }}
          placeholder="energy_price.gas"
        />
        <FieldError {...checks.say("key")} />
        <datalist id="assumption-keys">
          {plan.assumptions.map((a) => (
            <option key={a.key} value={a.key} />
          ))}
        </datalist>
      </label>
      <label className={styles.field}>
        <span>Value, and its unit</span>
        <span className={cls(own.inline)}>
          <input
            aria-label="Assumption value"
            inputMode="decimal"
            {...checks.field("value")}
            value={value}
            onChange={(event) => {
              checks.touch("value");
              setValue(event.target.value);
            }}
            placeholder="0.92"
          />
          <input
            aria-label="Assumption unit"
            {...checks.field("unit")}
            value={unit}
            onChange={(event) => {
              checks.touch("unit");
              setUnit(event.target.value);
            }}
          />
        </span>
        <FieldError {...checks.say("value")} />
        <FieldError {...checks.say("unit")} />
      </label>
      <label className={styles.field}>
        <span>Where does it come from?</span>
        <input
          aria-label="Assumption source"
          {...checks.field("source")}
          value={source}
          onChange={(event) => {
            checks.touch("source");
            setSource(event.target.value);
          }}
          placeholder="Procurement: at 1.5 times today's gas price, about 8% less gas burned"
        />
        <FieldError {...checks.say("source")} />
      </label>
      {first ? null : (
        <label className={styles.field}>
          <span>Why is there a new set of assumptions?</span>
          <input
            aria-label="Why is there a new set of assumptions?"
            {...checks.field("because")}
            value={because}
            onChange={(event) => {
              checks.touch("because");
              setBecause(event.target.value);
            }}
          />
          <FieldError {...checks.say("because")} />
        </label>
      )}
      <p className={styles.note}>
        A scenario multiplies the target&rsquo;s baseline by every input whose unit is{" "}
        <strong>multiplier</strong>. State an energy price&rsquo;s <em>effect</em> that way. A price
        in money is recorded and shown, and is not used: EmitIQ does not guess how a price moves
        your emissions.
      </p>
      <button
        type="button"
        className={styles.primary}
        disabled={record.isPending}
        data-testid="record-assumption"
        onClick={() => void submit()}
      >
        {record.isPending ? "Recording…" : "Record the new version"}
      </button>
      <WriteFailed error={refusal} of="the assumptions" onReload={reload} />
    </div>
  );
}

function ProjectionRow({
  projection,
  shown,
  onShow,
}: {
  projection: PlanProjection;
  /** Whether this version is the scenario drawn on the pathway; absent where nothing is drawn. */
  shown?: boolean;
  onShow?: () => void;
}) {
  return (
    <li
      className={cls(own.projection)}
      data-testid="projection"
      data-assumption-version={projection.assumption_version}
    >
      <span className={cls(own.versionHead)}>
        Assumptions version {projection.assumption_version}
        {projection.because ? ` · ${projection.because}` : ""}
      </span>
      {onShow && !projection.refusal ? (
        <button
          type="button"
          className={cls(own.drawButton)}
          aria-pressed={shown === true}
          onClick={onShow}
          data-testid="projection-draw"
        >
          {shown ? "Drawn on the pathway" : "Draw on the pathway"}
        </button>
      ) : null}
      {projection.refusal ? (
        <span className={cls(own.v)} data-testid="projection-refused">
          Could not be projected: {projection.refusal}
        </span>
      ) : (
        <>
          <span className={cls(own.halfValue, "num")}>
            {factorValue(projection.emissions)} {projection.unit} by {projection.horizon_year}
          </span>
          <span className={cls(own.halfNote)}>modelled — not measured</span>
          {projection.applied.length > 0 ? (
            <span className={cls(own.assumptionSource)}>
              Applied: {projection.applied.join(", ")}
            </span>
          ) : (
            <span className={cls(own.assumptionSource)}>
              No input is stated as a multiplier, so nothing moved the baseline.
            </span>
          )}
          {projection.not_applied.map((item) => (
            <span
              key={item.key}
              className={cls(own.assumptionSource)}
              data-testid="projection-not-applied"
            >
              Recorded, not used: {item.key} — {item.reason}
            </span>
          ))}
        </>
      )}
    </li>
  );
}

/** What each version of the assumptions makes the target's baseline become. **Modelled.** */
export function Scenarios({
  targetKey,
  shown,
  onShow,
}: {
  targetKey: string;
  /** The assumptions version drawn on the pathway beside this list, or `null` for none. */
  shown?: number | null;
  onShow?: (version: number | null) => void;
}) {
  const scenarios = useScenarios(targetKey);
  return (
    <Loaded query={scenarios} of="The scenarios" empty="No assumptions recorded.">
      {(run) => (
        <div data-testid="scenarios">
          <p className={styles.note}>
            Starting from {factorValue(run.baseline)} {run.baseline_unit}, the baseline of{" "}
            <strong>{run.target_key}</strong> (version {run.target_version}). {run.method}
          </p>
          <p className={styles.note} data-testid="scenario-not-inventory">
            <strong>These figures are not your inventory.</strong> Changing an assumption changes
            this list and nothing the inventory holds; a figure here has no document, no locked
            factor and no ledger entry behind it.
          </p>
          <ul className={cls(own.versions)}>
            {run.projections.map((projection) => (
              <ProjectionRow
                key={projection.assumption_version}
                projection={projection}
                shown={shown === projection.assumption_version}
                onShow={
                  onShow
                    ? () =>
                        onShow(shown === projection.assumption_version ? null : projection.assumption_version)
                    : undefined
                }
              />
            ))}
          </ul>
          <ApplyAsPlan />
          <CostLane />
        </div>
      )}
    </Loaded>
  );
}
